//Global imports
import React, { useState } from 'react';
import {
    CAccordion,
    CAccordionBody,
    CAccordionHeader,
    CAccordionItem,
    CButton,
    CCol,
    CFormCheck,
    CFormInput,
    CFormLabel,
    CFormTextarea,
    CRow,
} from '@coreui/react';
import JoditEditor from 'jodit-react';

//File imports
import {
    DocumentDetailPageErrors,
    DocumentDetailPageInfoData,
} from '../../Types/types';
import { Language } from '../../../../language/English';
import CTABox from '../../../../assets/images/cta_box.webp';
import HowTo from '../../../../assets/images/how_it_works.webp';
import DocumentPageBannerHeadingHint from '../../../../assets/images/document_page_banner_heading.webp';
import DocumentPageHighlightHint from '../../../../assets/images/document_page_highlight.webp';
import DocumentPageWithoutHighlightHint from '../../../../assets/images/document_page_without_highlight.webp';
import DocumentPageBenefit from '../../../../assets/images/document_page_benefit.webp';
import DocumentPageShortDescription from '../../../../assets/images/document_page_short_description.webp';
import DocumentPageDescription from '../../../../assets/images/document_page_description.webp';
import DocumentPageYoutubeLink from '../../../../assets/images/document_page_youtubelink.webp';
import StringUtils from '../../../../services/StringUtils/StringUtils';
import './CustomerReviews.css';
import './SeoKeywordTags.css';

//Component imports
import { FAQs, SeoFields } from '../../Components';
import {
    FieldHeadingWithHintText,
    FieldPreviewModal,
    HintText,
    LengthChecker,
} from '../../../../components';

interface DocumentDetailPageProps {
    onBlurUrlField: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onChageDescriptionText: (newContent: string) => void;
    setLoader: (status: boolean) => void;
    setUniqueFolderName: (name: string) => void;
    setShowFaqNote: (status: boolean) => void;
    handleHightLightChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => void;
    handleBenefitChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => void;

    data: DocumentDetailPageInfoData;
    errors: DocumentDetailPageErrors;
    stringUtils: StringUtils;
    name: string;
    editorConfig: any;
    editor: React.RefObject<any>;
    folderName: string | null;
    showFaqNote: boolean;
    handleShowHighlightOnDetailPage: (checked: boolean) => void;
    reviews: string;
    onChangeReviews: (newReviews: any[]) => void;
    reviewsErrors?: any[];
    tags: string;
    onChangeTags: (newTags: string[]) => void;
    setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const DocumentDetailPage: React.FC<DocumentDetailPageProps> = ({
    onBlurUrlField,
    handleChange,
    handlePaste,
    onKeyDown,
    onChageDescriptionText,
    setLoader,
    setUniqueFolderName,
    setShowFaqNote,
    handleHightLightChange,
    handleBenefitChange,

    data,
    errors,
    stringUtils,
    name,
    editorConfig,
    editor,
    folderName,
    showFaqNote,
    handleShowHighlightOnDetailPage,
    reviews,
    onChangeReviews,
    reviewsErrors = [],
    tags,
    onChangeTags,
    setErrors,
}) => {
    const [fieldPreviewModalVisible, setFieldPreviewModalVisible] =
        useState(false);
    const [hintImage, setHintImage] = useState<string>('');
    const [activeReviewKey, setActiveReviewKey] = useState<number | string | undefined>(undefined);

    const openAccordion = (index: number) => {
        const inputEl = document.getElementById(`reviewer-name-${index}`);
        if (inputEl) {
            const accordionItem = inputEl.closest('.accordion-item');
            if (accordionItem) {
                const button = accordionItem.querySelector('.accordion-button');
                if (button && !button.classList.contains('collapsed')) {
                    return;
                }
            }
        }
        setActiveReviewKey(undefined);
        setTimeout(() => {
            setActiveReviewKey(index);
        }, 50);
    };

    React.useEffect(() => {
        if (reviewsErrors && reviewsErrors.length > 0) {
            const firstInvalidIndex = reviewsErrors.findIndex(
                (err) => err && Object.keys(err).length > 0
            );
            if (firstInvalidIndex !== -1) {
                openAccordion(firstInvalidIndex);
                
                const err = reviewsErrors[firstInvalidIndex];
                let elementId = '';
                if (err.reviewerName) {
                    elementId = `reviewer-name-${firstInvalidIndex}`;
                } else if (err.rating) {
                    elementId = `rating-${firstInvalidIndex}`;
                } else if (err.reviewText) {
                    elementId = `review-text-${firstInvalidIndex}`;
                }
                
                if (elementId) {
                    setTimeout(() => {
                        const element = document.getElementById(elementId);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            element.focus();
                        }
                    }, 150);
                }
            }
        }
    }, [reviewsErrors]);

    const parsedReviews = React.useMemo(() => {
        try {
            return JSON.parse(reviews || '[]');
        } catch (e) {
            return [];
        }
    }, [reviews]);

    const parsedTags = React.useMemo(() => {
        try {
            return Array.isArray(tags) ? tags : JSON.parse(tags || '[]');
        } catch (e) {
            return [];
        }
    }, [tags]);

    const [tagInput, setTagInput] = useState('');

    const handleAddTag = (newTag: string) => {
        const trimmed = newTag.trim();
        if (!trimmed) return;
        if (parsedTags.includes(trimmed)) return;
        const updated = [...parsedTags, trimmed];
        onChangeTags(updated);
    };

    const handleDeleteTag = (tagToDelete: string) => {
        const updated = parsedTags.filter((t: string) => t !== tagToDelete);
        onChangeTags(updated);
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddTag(tagInput);
            setTagInput('');
        }
    };

    const handleAddReview = () => {
        if (parsedReviews.length >= 10) {
            return;
        }
        let reviewsErrorsList: any[] = [];
        let hasReviewsErrors = false;
        parsedReviews.forEach((rev: any, index: number) => {
            let revError: any = {};
            if (!rev.reviewerName || !rev.reviewerName.trim()) {
                revError.reviewerName = Language.REVIEWER_NAME_REQUIRED;
                hasReviewsErrors = true;
            }
            if (rev.rating === undefined || rev.rating === null || rev.rating === '' || isNaN(Number(rev.rating))) {
                revError.rating = Language.REVIEW_RATING_REQUIRED;
                hasReviewsErrors = true;
            } else {
                const r = Number(rev.rating);
                if (r < 4 || r > 5) {
                    revError.rating = Language.REVIEW_RATING_VALIDATION_RANGE;
                    hasReviewsErrors = true;
                }
            }
            if (!rev.reviewText || !rev.reviewText.trim()) {
                revError.reviewText = Language.REVIEW_TEXT_REQUIRED;
                hasReviewsErrors = true;
            }
            reviewsErrorsList[index] = revError;
        });

        if (hasReviewsErrors) {
            setErrors((prev: any) => ({
                ...prev,
                reviews: reviewsErrorsList,
            }));
            return;
        }

        setErrors((prev: any) => {
            const updated = { ...prev };
            delete updated.reviews;
            return updated;
        });

        const newIndex = parsedReviews.length;
        const newReview = {
            reviewerName: '',
            rating: 5,
            reviewText: '',
        };
        onChangeReviews([...parsedReviews, newReview]);
        openAccordion(newIndex);
        setTimeout(() => {
            const element = document.getElementById(`reviewer-name-${newIndex}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
            }
        }, 150);
    };

    const handleUpdateReview = (index: number, field: string, value: any) => {
        const updated = parsedReviews.map((rev: any, idx: number) => {
            if (idx === index) {
                return {
                    ...rev,
                    [field]: field === 'rating' ? (value === '' ? '' : parseFloat(value) || 0) : value,
                };
            }
            return rev;
        });
        onChangeReviews(updated);

        if (reviewsErrors[index]?.[field]) {
            setErrors((prev: any) => {
                const updatedReviewsErrors = [...(prev.reviews ?? [])];
                if (updatedReviewsErrors[index]) {
                    updatedReviewsErrors[index] = {
                        ...updatedReviewsErrors[index],
                        [field]: undefined,
                    };
                }
                return {
                    ...prev,
                    reviews: updatedReviewsErrors,
                };
            });
        }
    };

    const handleDeleteReview = (index: number) => {
        const updated = parsedReviews.filter((_: any, idx: number) => idx !== index);
        onChangeReviews(updated);

        setErrors((prev: any) => {
            const updatedReviewsErrors = (prev.reviews ?? []).filter((_: any, idx: number) => idx !== index);
            return {
                ...prev,
                reviews: updatedReviewsErrors,
            };
        });
    };

    const openHintModal = (imageSource: string) => {
        setHintImage(imageSource);
        setFieldPreviewModalVisible(true);
    };
    return (
        <>
            {/* Url, Google Meta Title, Meta Keyword, Meta Description Fields */}
            <SeoFields
                name={name}
                stringUtils={stringUtils}
                data={{
                    metaTitle: data?.metaTitle,
                    metaKeyword: data?.metaKeyword,
                    metaDescription: data?.metaDescription,
                }}
                errors={{
                    metaTitle: errors?.metaTitle,
                    metaKeyword: errors?.metaKeyword,
                    metaDescription: errors?.metaDescription,
                }}
                onBlurUrlField={onBlurUrlField}
                handleChange={handleChange}
                handlePaste={handlePaste}
                onKeyDown={onKeyDown}
            />

            {/* Banner heading */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.BANNER_HEADING}
                openModal={() => {
                    openHintModal(DocumentPageBannerHeadingHint);
                }}
            />
            <CFormInput
                value={data?.bannerHeading || ''}
                invalid={!!errors?.bannerHeading}
                feedback={errors?.bannerHeading}
                className="mt-2"
                type="text"
                name={stringUtils.convertToCamelCase(Language.BANNER_HEADING)}
                placeholder={Language.BANNER_HEADING}
                onChange={handleChange}
                maxLength={60}
            />
            <LengthChecker
                currentLength={data?.bannerHeading?.length || 0}
                maxLength={60}
            />

            {/* Short Description */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.SHORT_DESCRIPTION}
                openModal={() => {
                    openHintModal(DocumentPageShortDescription);
                }}
            />
            <HintText marginBottom="10px">
                {Language.SHORT_DESCRIPTION_HINT}
            </HintText>
            <CFormTextarea
                placeholder={Language.ENTER_SHORT_DESCRIPTION}
                onKeyDown={onKeyDown}
                onChange={handleChange}
                onPaste={handlePaste}
                value={data?.shortDescription}
                invalid={!!errors?.shortDescription}
                feedback={errors?.shortDescription}
                maxLength={1000}
                style={{ height: 180 }}
                name={stringUtils.convertToCamelCase(
                    Language.SHORT_DESCRIPTION,
                )}
            />
            <LengthChecker
                currentLength={data?.shortDescription?.length ?? 0}
                maxLength={1000}
            />

            {/* Document Highlights */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DOCUMENT_HIGHLIGHTS}
                openModal={() => {
                    openHintModal(DocumentPageHighlightHint);
                }}
            />

            <CRow className="d-flex align-items-center">
                <div className="d-flex align-items-center gap-2">
                    <CFormCheck
                        id="showHighlightOnDetailPage"
                        reverse
                        checked={data.showHighlightOnDetailPage}
                        label={Language.VISIBLE_ON_DOCUMENT_DETAIL_PAGE}
                        className="show-highlight-on-detail-page-checkbox"
                        onChange={(e) =>
                            handleShowHighlightOnDetailPage(e.target.checked)
                        }
                    />

                    <CButton
                        color="link"
                        size="sm"
                        className="text-decoration-none p-0 fs-mobile-8 text-end"
                        onClick={() => {
                            openHintModal(
                                data.showHighlightOnDetailPage
                                    ? DocumentPageHighlightHint
                                    : DocumentPageWithoutHighlightHint,
                            );
                        }}
                    >
                        {`${Language.SEE_PREVIEW} ${data.showHighlightOnDetailPage ? Language.WITH : Language.WITHOUT} ${Language.HIGHLIGHT.toLowerCase()}`}
                    </CButton>
                </div>
            </CRow>

            {data?.showHighlightOnDetailPage && (
                <>
                    <CAccordion alwaysOpen className="mt-2">
                        {data?.highlights &&
                            Array.isArray(data?.highlights) &&
                            data?.highlights.map((item, index) => (
                                <CAccordionItem key={index} itemKey={index}>
                                    <CAccordionHeader className="fw-semibold">
                                        {Language.HIGHLIGHT} {index + 1}
                                        {item.title && (
                                            <span className="ms-2 text-muted">
                                                — {item.title}
                                            </span>
                                        )}
                                    </CAccordionHeader>
                                    <CAccordionBody>
                                        <CFormInput
                                            value={item.title}
                                            onChange={(e) => {
                                                handleHightLightChange(
                                                    e,
                                                    'title',
                                                    index,
                                                );
                                            }}
                                            placeholder={Language.TITLE}
                                            maxLength={21}
                                        />
                                        <LengthChecker
                                            currentLength={item.title.length}
                                            maxLength={21}
                                        />

                                        <CFormInput
                                            className="mt-2"
                                            value={item.description}
                                            onChange={(e) => {
                                                handleHightLightChange(
                                                    e,
                                                    'description',
                                                    index,
                                                );
                                            }}
                                            name={stringUtils.convertToCamelCase(
                                                Language.DOCUMENT_HIGHLIGHTS,
                                            )}
                                            placeholder={Language.DESCRIPTION}
                                            maxLength={70}
                                        />
                                        <LengthChecker
                                            currentLength={
                                                item.description.length
                                            }
                                            maxLength={70}
                                        />
                                    </CAccordionBody>
                                </CAccordionItem>
                            ))}
                    </CAccordion>
                    {errors?.highlights && (
                        <div className="alert alert-danger">
                            {errors?.highlights}
                        </div>
                    )}
                </>
            )}

            {/* Document Benefits */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DOCUMENT_BENEFITS}
                openModal={() => {
                    openHintModal(DocumentPageBenefit);
                }}
            />
            <CAccordion alwaysOpen className="mt-2">
                {data?.benefits &&
                    Array.isArray(data?.benefits) &&
                    data?.benefits.map((item, index) => (
                        <CAccordionItem key={index} itemKey={index}>
                            <CAccordionHeader className="fw-semibold">
                                {Language.BENEFIT} {index + 1}
                                {item.title && (
                                    <span className="ms-2 text-muted">
                                        — {item.title}
                                    </span>
                                )}
                            </CAccordionHeader>
                            <CAccordionBody>
                                <CFormInput
                                    value={item.title}
                                    onChange={(e) => {
                                        handleBenefitChange(e, 'title', index);
                                    }}
                                    placeholder={Language.TITLE}
                                    maxLength={24}
                                />
                                <LengthChecker
                                    currentLength={item.title.length}
                                    maxLength={24}
                                />

                                <CFormInput
                                    className="mt-2"
                                    value={item.description}
                                    onChange={(e) => {
                                        handleBenefitChange(
                                            e,
                                            'description',
                                            index,
                                        );
                                    }}
                                    name={stringUtils.convertToCamelCase(
                                        Language.DOCUMENT_BENEFITS,
                                    )}
                                    placeholder={Language.DESCRIPTION}
                                    maxLength={60}
                                />

                                <LengthChecker
                                    currentLength={item.description.length}
                                    maxLength={60}
                                />
                            </CAccordionBody>
                        </CAccordionItem>
                    ))}
            </CAccordion>
            {errors?.benefits && (
                <div className="alert alert-danger">{errors?.benefits}</div>
            )}

            {/* Description */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DESCRIPTION}
                openModal={() => {
                    openHintModal(DocumentPageDescription);
                }}
            />
            <HintText marginBottom="10px">{Language.DESCRIPTION_HINT}</HintText>

            <div className="mb-3 p-3 rounded border bg-light">
                <h6>
                    <strong>
                        {Language.HOWTO_USE_DESCRIPTION_PLACEHOLDER}:
                    </strong>
                </h6>
                <ul>
                    <li>
                        <code>{Language.CTA_BANNER}</code>
                        {Language.CTA_BANNER_HINT}
                    </li>
                    <li>
                        <code>{Language.DOCUMENT_STEPS}</code>
                        {Language.DOCUMENT_STEPS_HINT}
                    </li>
                </ul>
                <p>{Language.PLACE_PLACEHOLDERS}</p>

                <div className="row mt-3">
                    <div className="col-md-6 mb-2">
                        <h6 className="mb-2">
                            Placeholder: <code>{Language.CTA_BANNER}</code>
                        </h6>
                        <img
                            src={CTABox}
                            style={{
                                objectFit: 'contain',
                            }}
                            alt="CTA Banner Example"
                            className="img-fluid rounded border"
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <h6 className="mb-2">
                            Placeholder: <code> {Language.DOCUMENT_STEPS}</code>
                        </h6>
                        <img
                            src={HowTo}
                            style={{
                                objectFit: 'contain',
                            }}
                            alt="Document Steps Example"
                            className="img-fluid rounded border"
                        />
                    </div>
                </div>
            </div>

            <div id="description">
                <JoditEditor
                    ref={editor}
                    value={data?.description}
                    config={editorConfig}
                    onBlur={(newContent) => onChageDescriptionText(newContent)}
                />
            </div>
            {errors?.description && (
                <div className="alert alert-danger">{errors?.description}</div>
            )}

            {/* YouTube Link */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.YOUTUBE_LINK}
                openModal={() => {
                    openHintModal(DocumentPageYoutubeLink);
                }}
            />
            <HintText marginBottom="10px">{Language.YOUTUBE_HINT}</HintText>
            <CFormInput
                type="text"
                placeholder={Language.ENTER_YOUTUBE_VIDEO_LINK}
                onChange={handleChange}
                value={data?.youtubeLink}
                invalid={!!errors?.youtubeLink}
                feedback={errors?.youtubeLink}
                name={stringUtils.convertToCamelCase(Language.YOUTUBE_LINK)}
            />

            {/* FAQs Section */}
            <FAQs
                folderName={folderName}
                faqValidationError={errors?.faqs}
                setLoader={(status: boolean) => setLoader(status)}
                setUniqueFolderName={(name: string) =>
                    setUniqueFolderName(name)
                }
                showFaqNote={showFaqNote}
                setShowFaqNote={(status: boolean) => setShowFaqNote(status)}
            />

            {/* Customer Reviews Section */}
            <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                <CFormLabel className="mb-0 responsive-review-header h5 fw-semibold">{Language.CUSTOMER_REVIEWS} ({parsedReviews.length}/10)</CFormLabel>
                {parsedReviews.length > 0 && parsedReviews.length < 10 && (
                    <CButton 
                        color="primary" 
                        size="sm" 
                        className="responsive-review-btn"
                        onClick={handleAddReview}
                    >
                        {Language.ADD_REVIEW}
                    </CButton>
                )}
            </div>
            
            {parsedReviews.length === 0 ? (
                <div className="text-center py-5 border rounded bg-light mt-2 mb-4">
                    <p className="text-muted mb-3">{Language.NO_REVIEWS_ADDED_YET}</p>
                    <CButton 
                        color="primary" 
                        onClick={handleAddReview}
                    >
                        {Language.ADD_REVIEW}
                    </CButton>
                </div>
            ) : (
                <CAccordion key={activeReviewKey !== undefined ? String(activeReviewKey) : 'empty'} alwaysOpen className="mt-2 mb-4" activeItemKey={activeReviewKey}>
                    {parsedReviews.map((item: any, index: number) => {
                        const reviewError = reviewsErrors[index] ?? {};
                        const hasError = !!(reviewError.reviewerName || reviewError.rating || reviewError.reviewText);
                        return (
                            <CAccordionItem key={index} itemKey={index}>
                                <CAccordionHeader className="fw-semibold">
                                    <span className={hasError ? "text-danger" : ""}>
                                        {Language.REVIEW} {index + 1}
                                        {item.reviewerName ? ` — ${item.reviewerName}` : ''}
                                        {item.rating ? ` (${item.rating} ★)` : ''}
                                        {hasError ? ` (⚠️ ${Language.REVIEW_VALIDATION_ERROR})` : ''}
                                    </span>
                                </CAccordionHeader>
                                <CAccordionBody>
                                    <div className="d-flex justify-content-end mb-2">
                                        <CButton 
                                            color="danger" 
                                            size="sm" 
                                            className="text-white"
                                            onClick={() => handleDeleteReview(index)}
                                        >
                                            {Language.DELETE}
                                        </CButton>
                                    </div>
                                    <CRow className="mb-3">
                                        <CCol md={8} className="responsive-reviewer-name-col">
                                            <label className="form-label fw-semibold">{Language.REVIEWER_NAME}</label>
                                            <CFormInput
                                                id={`reviewer-name-${index}`}
                                                value={item.reviewerName || ''}
                                                onChange={(e) => handleUpdateReview(index, 'reviewerName', e.target.value)}
                                                placeholder={Language.ENTER_REVIEWER_NAME_PLACEHOLDER}
                                                invalid={!!reviewError.reviewerName}
                                                feedback={reviewError.reviewerName}
                                            />
                                        </CCol>
                                        <CCol md={4}>
                                            <label className="form-label fw-semibold">{Language.RATING_LABEL_WITH_RANGE}</label>
                                            <CFormInput
                                                id={`rating-${index}`}
                                                type="number"
                                                step="0.1"
                                                min="4"
                                                max="5"
                                                value={item.rating || ''}
                                                onChange={(e) => handleUpdateReview(index, 'rating', e.target.value)}
                                                placeholder="e.g. 4.5"
                                                invalid={!!reviewError.rating}
                                                feedback={reviewError.rating}
                                            />
                                        </CCol>
                                    </CRow>
                                    <CRow>
                                        <CCol md={12}>
                                            <label className="form-label fw-semibold">{Language.REVIEW_TEXT}</label>
                                            <CFormTextarea
                                                id={`review-text-${index}`}
                                                value={item.reviewText || ''}
                                                onChange={(e) => handleUpdateReview(index, 'reviewText', e.target.value)}
                                                placeholder={Language.ENTER_REVIEW_TEXT_PLACEHOLDER}
                                                rows={3}
                                                invalid={!!reviewError.reviewText}
                                                feedback={reviewError.reviewText}
                                            />
                                        </CCol>
                                    </CRow>
                                </CAccordionBody>
                            </CAccordionItem>
                        );
                    })}
                </CAccordion>
            )}

            {/* SEO Keyword Tags Section */}
            <div className="mt-4 mb-4 border rounded p-4 bg-white shadow-sm">
                <h5 className="fw-semibold mb-1 d-flex align-items-center">
                    <span className="me-2">🏷️</span> {Language.SEO_KEYWORD_TAGS}
                </h5>
                <p className="text-muted small mb-3">
                    {Language.SEO_KEYWORD_TAGS_HINT}
                </p>
                
                <div className="mb-3">
                    <CFormInput
                        type="text"
                        placeholder={Language.TYPE_TAG_PLACEHOLDER}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                    />
                </div>
                
                {parsedTags.length === 0 ? (
                    <div className="text-muted small py-2">{Language.NO_TAGS_ADDED_YET}</div>
                ) : (
                    <div className="d-flex flex-wrap gap-2 py-1">
                         {parsedTags.map((tag: string, idx: number) => (
                            <span 
                                key={idx} 
                                className="d-inline-flex align-items-center bg-light text-dark rounded-pill border seo-tag-badge"
                            >
                                {tag}
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ms-2 text-muted border-0 seo-tag-delete-btn"
                                    onClick={() => handleDeleteTag(tag)}
                                    aria-label={Language.REMOVE_TAG}
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <FieldPreviewModal
                visible={fieldPreviewModalVisible}
                onClose={() => setFieldPreviewModalVisible(false)}
                imageSrc={hintImage}
            />
        </>
    );
};

export default DocumentDetailPage;
